<template>
  <el-dialog title="查看考试" :visible.sync="dialogVisible" width="800px" append-to-body @close="handleClose">
    <div v-if="examData" class="exam-view">
      <div class="exam-header">
        <h3>{{ examData.title }}</h3>
        <div class="exam-meta">
          <el-tag :type="examData.enabled ? 'success' : 'danger'" size="small">
            {{ examData.enabled ? "启用" : "禁用" }}
          </el-tag>
          <el-tag size="small">{{ trainingCategoryText(examData.category) || "未分类" }}</el-tag>
          <el-tag v-for="item in examData.tags" :key="item" size="small" type="info">{{ item }}</el-tag>
          <template v-if="resultMode">
            <span class="question-count">本次题目 {{ displayQuestions.length }} 题</span>
          </template>
          <template v-else>
            <span class="question-count">题库 {{ bankCount }} 题</span>
            <span class="question-count">出题 {{ paperCount }} 题</span>
          </template>
          <span class="score-info">
            总分: {{ examData.totalScore }}分 | 及格分: {{ examData.passScore }}分
            <template v-if="resultMode && examScore != null">
              | 得分: <span :class="scorePassed ? 'score-pass' : 'score-fail'">{{ examScore }}分</span>
            </template>
          </span>
        </div>
        <div v-if="hasExamTiming" class="exam-timing">
          <span>开始考试：{{ formatExamTime(examStartTime) }}</span>
          <span>提交考试：{{ formatExamTime(examEndTime) }}</span>
          <span>考试用时：{{ examDurationText }}</span>
        </div>
      </div>

      <div v-if="drawRuleText && !resultMode" class="draw-rules">{{ drawRuleText }}</div>

      <div v-if="examFaces.length" class="face-section">
        <h4>人脸核验</h4>
        <div class="face-summary">
          共抓拍 {{ examFaces.length }} 次，通过 {{ facePassedCount }} 次
        </div>
        <div class="face-list">
          <div v-for="(item, index) in examFaces" :key="index" class="face-item">
            <div class="face-photo">
              <file-preview v-if="item.url" :value="item.url" />
            </div>
            <div class="face-meta">
              <div class="face-title">{{ faceCaptureTitle(item, index) }}</div>
              <div class="face-time">{{ item.captureTime | dateFormat('MM-dd hh:mm:ss') }}</div>
              <el-tag :type="faceResultType(item)" size="mini">
                {{ faceResultText(item) }}
              </el-tag>
              <div class="face-result">{{ item.validateResultText || "暂无核验结果" }}</div>
              <div v-if="item.similarity != null" class="face-similarity">
                相似度：{{ item.similarity }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="displayQuestions.length > 0" class="questions-section">
        <h4>考试题目</h4>
        <div class="question-list">
          <div v-for="(question, index) in displayQuestions" :key="question.id || index" class="question-item">
            <div class="question-header">
              <span class="question-number">第{{ index + 1 }}题</span>
              <div class="question-info">
                <el-tag v-if="question.category" size="mini" type="warning">{{
                  question.category
                }}</el-tag>
                <el-tag size="mini" :type="question.type === QUESTION_TYPE.SINGLE_CHOICE ? 'primary' : 'success'">
                  {{ QUESTION_TYPE_MAP[question.type] || "未知类型" }}
                </el-tag>
                <span class="question-score">{{ question.score }}分</span>
              </div>
            </div>

            <div class="question-content">
              <div class="question-text">{{ question.question }}</div>
              <div v-if="question.medias && question.medias.length > 0" class="question-medias">
                <file-preview v-for="media in question.medias" :key="media" :value="media" />
              </div>

              <div v-if="question.options && question.options.length > 0" class="question-options">
                <div v-for="option in question.options" :key="option.id">
                  <div class="option-item" :class="{
                    'correct-answer': isCorrectAnswer(question, option.id),
                    'user-answer': isUserAnswer(question, option.id)
                  }">
                    <span class="option-label">{{ option.id }}.</span>
                    <span class="option-content">{{ option.content }}</span>
                    <span v-if="isUserWrong(question, option.id)" class="choice-tag">司机选择</span>
                    <i v-if="isCorrectAnswer(question, option.id)" class="el-icon-check correct-icon"></i>
                    <i v-else-if="isUserWrong(question, option.id)" class="el-icon-close wrong-icon"></i>
                  </div>
                  <div v-if="option.medias && option.medias.length > 0" class="option-medias">
                    <file-preview v-for="media in option.medias" :key="media" :value="media" />
                  </div>
                </div>
              </div>

              <!-- 显示用户答案和正确答案 -->
              <div v-if="resultMode || hasUserAnswer(question)" class="answer-info">
                <div class="answer-item">
                  <span class="answer-label">司机选择：</span>
                  <span class="answer-value user-answer-text">{{
                    getUserAnswerText(question)
                  }}</span>
                </div>
                <div class="answer-item">
                  <span class="answer-label">正确答案：</span>
                  <span class="answer-value correct-answer-text">{{
                    getCorrectAnswerText(question)
                  }}</span>
                </div>
                <div class="answer-item">
                  <span class="answer-label">答题结果：</span>
                  <el-tag :type="answerResultType(question)" size="small" class="answer-status">
                    {{ answerResultText(question) }}
                  </el-tag>
                </div>
              </div>

              <div v-if="question.explanation" class="question-explanation">
                <h5>题目解析：</h5>
                <p>{{ question.explanation }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="resultMode" class="questions-section">
        <h4>考试题目</h4>
        <div class="draw-rules">没有找到这次抽到的题目</div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { QUESTION_TYPE, QUESTION_TYPE_MAP } from "@/core/const/global-const";
import { DateUtil } from "@/util";
import { categoryLabel, paperQuestionCount } from "../utils/examPaper";
import trainingCategoryMixin from "../utils/trainingCategory";

export default {
  name: "ExamViewDialog",
  mixins: [trainingCategoryMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    examData: {
      type: Object,
      default: () => ({})
    },

    userAnswerMap: {
      type: Object,
      default: () => ({})
    },
    faceCaptures: {
      type: Array,
      default: () => []
    },
    examStartTime: {
      default: null
    },
    examEndTime: {
      default: null
    },
    examDuration: {
      default: null
    },
    examScore: {
      default: null
    },
    paperQuestionIds: {
      type: Array,
      default: () => []
    },
    resultMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    examFaces() {
      return (this.faceCaptures || []).filter((item) => item && item.isInExam);
    },
    facePassedCount() {
      return this.examFaces.filter((item) => item.facePassed).length;
    },
    bankCount() {
      return this.examData && this.examData.questions ? this.examData.questions.length : 0;
    },
    paperCount() {
      return paperQuestionCount(this.examData || {});
    },
    hasExamTiming() {
      return this.examStartTime || this.examEndTime || this.examDuration != null;
    },
    examDurationText() {
      if (this.examDuration == null) {
        return "—";
      }
      return this.examDuration + "分钟";
    },
    scorePassed() {
      if (this.examScore == null || !this.examData) {
        return false;
      }
      return Number(this.examScore) >= Number(this.examData.passScore);
    },
    displayQuestions() {
      const questions = (this.examData && this.examData.questions) || [];
      if (!this.resultMode) {
        return questions;
      }
      const ids = this.drawnQuestionIds;
      if (!ids.length) {
        return questions;
      }
      const questionMap = {};
      questions.forEach((question) => {
        if (question && question.id) {
          questionMap[question.id] = question;
        }
      });
      return ids.map((id) => questionMap[id]).filter(Boolean);
    },
    drawnQuestionIds() {
      const ids = this.paperQuestionIds || [];
      if (ids.length) {
        return ids;
      }
      if (!this.resultMode || !this.drawRuleText) {
        return [];
      }
      return Object.keys(this.userAnswerMap || {});
    },
    drawRuleText() {
      const rules = (this.examData && this.examData.drawRules) || [];
      if (!rules.length) {
        return "";
      }
      return rules
        .map((rule) => `${categoryLabel(rule.category)}抽${rule.count}题`)
        .join("，");
    }
  },
  data() {
    return {
      QUESTION_TYPE,
      QUESTION_TYPE_MAP,
      dialogVisible: false
    };
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
      },
      immediate: true
    }
  },
  methods: {
    formatExamTime(value) {
      if (!value) {
        return "—";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "—";
      }
      return DateUtil.format(date, "MM-dd hh:mm:ss");
    },
    faceCaptureTitle(item, index) {
      if (item && item.beforeExam) {
        return "考试前核验";
      }
      return `第${index + 1}次抓拍`;
    },
    faceResultText(item) {
      if (!item || item.facePassed == null) {
        return "核验中";
      }
      return item.facePassed ? "核验通过" : "核验未通过";
    },
    faceResultType(item) {
      if (!item || item.facePassed == null) {
        return "info";
      }
      return item.facePassed ? "success" : "danger";
    },
    // 判断是否为正确答案
    isCorrectAnswer(question, optionId) {
      return question.answer && question.answer.includes(optionId);
    },

    // 判断是否为用户答案
    isUserAnswer(question, optionId) {
      if (!this.userAnswerMap || !question.id) {
        return false;
      }
      const userAnswers = this.userAnswerMap[question.id];
      return userAnswers && Array.isArray(userAnswers) && userAnswers.includes(optionId);
    },

    // 司机选了、但不是正确答案
    isUserWrong(question, optionId) {
      return this.isUserAnswer(question, optionId) && !this.isCorrectAnswer(question, optionId);
    },

    // 判断是否有用户答案
    hasUserAnswer(question) {
      if (!this.userAnswerMap || !question.id) {
        return false;
      }
      const userAnswers = this.userAnswerMap[question.id];
      return userAnswers && Array.isArray(userAnswers) && userAnswers.length > 0;
    },

    // 获取用户答案文字
    getUserAnswerText(question) {
      if (!this.hasUserAnswer(question)) {
        return "未作答";
      }
      const userAnswers = this.userAnswerMap[question.id];
      if (!question.options || !Array.isArray(userAnswers)) {
        return "未作答";
      }
      const answerTexts = userAnswers
        .map((answerId) => {
          const option = question.options.find((opt) => opt.id === answerId);
          return option ? `${option.id}. ${option.content}` : answerId;
        })
        .filter(Boolean);
      return answerTexts.length > 0 ? answerTexts.join("；") : "未作答";
    },

    // 获取正确答案文字
    getCorrectAnswerText(question) {
      if (!question.answer || !Array.isArray(question.answer) || question.answer.length === 0) {
        return "无";
      }
      if (!question.options) {
        return question.answer.join("、");
      }
      const answerTexts = question.answer
        .map((answerId) => {
          const option = question.options.find((opt) => opt.id === answerId);
          return option ? `${option.id}. ${option.content}` : answerId;
        })
        .filter(Boolean);
      return answerTexts.length > 0 ? answerTexts.join("；") : question.answer.join("、");
    },

    // 判断答案是否正确
    answerResultText(question) {
      if (!this.hasUserAnswer(question)) {
        return "未作答";
      }
      return this.isAnswerCorrect(question) ? "回答正确" : "回答错误";
    },
    answerResultType(question) {
      if (!this.hasUserAnswer(question)) {
        return "info";
      }
      return this.isAnswerCorrect(question) ? "success" : "danger";
    },
    isAnswerCorrect(question) {
      if (!this.hasUserAnswer(question) || !question.answer) {
        return false;
      }
      const userAnswers = this.userAnswerMap[question.id];
      const correctAnswers = Array.isArray(question.answer) ? question.answer : [question.answer];

      // 转换为字符串数组并排序后比较
      const userAnswersSorted = [...userAnswers].sort().join(",");
      const correctAnswersSorted = [...correctAnswers].sort().join(",");

      return userAnswersSorted === correctAnswersSorted;
    },

    // 关闭
    handleClose() {
      this.dialogVisible = false;
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style lang="less" scoped>
.exam-view {
  .exam-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e4e7ed;

    h3 {
      margin: 0 0 12px 0;
      color: #303133;
      font-size: 20px;
    }

    .exam-meta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      .question-count {
        margin-left: 16px;
        color: #409eff;
        font-weight: 600;
      }

      .score-info {
        margin-left: 16px;
        color: #909399;
        font-size: 14px;

        .score-pass {
          color: #67c23a;
          font-weight: 600;
        }

        .score-fail {
          color: #f56c6c;
          font-weight: 600;
        }
      }
    }

    .exam-timing {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-top: 12px;
      color: #606266;
      font-size: 14px;
    }
  }

  .draw-rules {
    margin-bottom: 16px;
    color: #606266;
  }

  .face-section {
    margin-bottom: 20px;

    h4 {
      margin: 0 0 12px 0;
      color: #303133;
      font-size: 16px;
    }

    .face-summary {
      margin-bottom: 12px;
      color: #606266;
      font-size: 14px;
    }

    .face-list {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }

    .face-item {
      display: flex;
      width: 360px;
      padding: 12px;
      border: 1px solid #e4e7ed;
      border-radius: 6px;
      background: #fafafa;
    }

    .face-photo {
      width: 96px;
      height: 120px;
      margin-right: 12px;
      flex-shrink: 0;

      /deep/ .image-preview,
      /deep/ .el-image {
        width: 96px;
        height: 120px;
        max-width: 96px;
        max-height: 120px;
      }
    }

    .face-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      min-width: 0;
    }

    .face-title {
      color: #303133;
      font-weight: 600;
    }

    .face-time,
    .face-result,
    .face-similarity {
      color: #606266;
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .questions-section {
    h4 {
      margin: 0 0 16px 0;
      color: #303133;
      font-size: 16px;
    }

    .question-list {
      .question-item {
        border: 1px solid #e4e7ed;
        border-radius: 6px;
        margin-bottom: 16px;
        padding: 16px;

        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .question-number {
            font-weight: 600;
            color: #303133;
          }

          .question-info {
            display: flex;
            align-items: center;
            gap: 8px;

            .question-score {
              margin-left: 12px;
              color: #67c23a;
              font-weight: 600;
            }
          }
        }

        .question-content {
          .question-text {
            margin-bottom: 16px;
            line-height: 1.6;
            color: #303133;
            font-size: 15px;
          }

          .question-medias {
            margin-bottom: 16px;
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .question-options {
            margin-bottom: 16px;

            .option-item {
              display: flex;
              align-items: center;
              margin-bottom: 8px;
              padding: 8px;
              border-radius: 4px;
              transition: background-color 0.3s;

              &.correct-answer {
                background-color: #f0f9ff;
                border: 1px solid #67c23a;
              }

              &.user-answer {
                background-color: #fff7e6;
                border: 1px solid #e6a23c;
              }

              &.correct-answer.user-answer {
                background-color: #f0f9ff;
                border: 1px solid #67c23a;
              }

              .choice-tag {
                margin-left: 8px;
                color: #f56c6c;
                font-size: 12px;
                font-weight: 600;
              }

              .wrong-icon {
                color: #f56c6c;
                font-size: 16px;
                margin-left: 8px;
              }

              .option-label {
                font-weight: 600;
                margin-right: 8px;
                min-width: 20px;
              }

              .option-content {
                color: #606266;
                flex: 1;
              }

              .correct-icon {
                color: #67c23a;
                font-size: 16px;
                margin-left: 8px;
              }
            }

            .option-medias {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
            }
          }

          .answer-info {
            background: #f5f7fa;
            padding: 12px;
            border-radius: 4px;
            margin-bottom: 16px;
            border-left: 4px solid #409eff;

            .answer-item {
              display: flex;
              align-items: center;
              margin-bottom: 8px;

              &:last-child {
                margin-bottom: 0;
              }

              .answer-label {
                font-weight: 600;
                color: #303133;
                min-width: 80px;
                font-size: 14px;
              }

              .answer-value {
                color: #606266;
                font-size: 14px;
                flex: 1;
              }

              .user-answer-text {
                color: #e6a23c;
                font-weight: 500;
              }

              .correct-answer-text {
                color: #67c23a;
                font-weight: 500;
              }

              .answer-status {
                margin-left: 0;
              }
            }
          }

          .question-explanation {
            background: #f8f9fa;
            padding: 12px;
            border-radius: 4px;
            border-left: 4px solid #409eff;

            h5 {
              margin: 0 0 8px 0;
              color: #303133;
              font-size: 14px;
            }

            p {
              margin: 0;
              color: #606266;
              line-height: 1.6;
            }
          }
        }
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style>
